<script setup lang="ts">
import { ref, computed, watch, nextTick } from "vue";
import { DiscussionComment, DiscussionProperties, UserInfoDto } from "~/api";
import { formatDate } from "~/utils/dateUtils";
import { plural } from "~/utils/miscUtils";
import DiscussionThreadComment from "./DiscussionThreadComment.vue";
import DiscussionThreadEditor from "./DiscussionThreadEditor.vue";
import { isCommentEmpty, sanitizeCommentHtml } from "./discussionRichText";

const props = defineProps<{
  comment: DiscussionComment;
  colorHierarchy: string[];
  authorColorMap?: Record<string, string>;
  currentUser: UserInfoDto;
  currentUserGuid: string;
  properties: DiscussionProperties;
  readOnly: boolean;
  currentDepth: number;
  substituteTemplatedText: (
    text: string | undefined | null,
  ) => string | undefined | null;
}>();

const emit = defineEmits<{
  (e: "reply", parentId: number, reply: DiscussionComment): void;
}>();

const showReply = ref(false);
const replyText = ref("");
const editing = ref<DiscussionComment>();
const collapsed = ref(props.properties?.startCollapsed || false);
const maxDepth = 5;

const getAuthorColor = (author: string) => {
  if (!props.authorColorMap) {
    return props.colorHierarchy[0];
  }
  return props.authorColorMap[author] || props.colorHierarchy[0];
};

const commentColor = computed(() => getAuthorColor(props.comment.authorGuid));

const commentHtml = computed(() => sanitizeCommentHtml(props.comment.text));

const isReplyEmpty = computed(() => isCommentEmpty(replyText.value));

const submitReply = () => {
  const reply: DiscussionComment = {
    id: editing.value ? editing.value.id : Date.now(),
    authorName: props.currentUser.loginName,
    authorGuid: props.currentUser.userGuid,
    text: replyText.value,
    createdAt: new Date().toISOString(),
    editedAt: editing.value ? new Date().toISOString() : undefined,
    replies: editing.value ? editing.value.replies : [],
  };

  emit("reply", props.comment.id, reply);
  replyText.value = "";
  showReply.value = false;
  editing.value = undefined;

  collapsed.value = false; // Expand the comment thread after replying
};

const handleCancel = () => {
  showReply.value = false;
  replyText.value = "";
  editing.value = undefined;
};

/**
 * These watchers are used to scroll to the reply area when the user clicks on the reply/edit button.
 * This prevents confusion if the user clicks on the reply button and the reply area is not visible.
 *
 * The editor is a TinyMCE iframe, so there is no plain input to focus here —
 * scrolling the wrapper into view is what makes the reply area discoverable.
 *
 * A template ref rather than a document lookup: this component recurses for
 * every reply, so a shared id would appear many times in the thread and
 * getElementById would scroll to whichever came first in document order rather
 * than the comment the user actually clicked.
 */
const replyAreaRef = ref<HTMLElement>();

const scrollToReplyArea = async () => {
  await nextTick(); // need to wait for DOM to update
  replyAreaRef.value?.scrollIntoView({ behavior: "smooth", block: "center" });
};

watch(
  () => editing.value,
  async (newComment) => {
    if (newComment) {
      replyText.value = newComment.text;
      await scrollToReplyArea();
    }
  },
);

watch(
  () => showReply.value,
  async (newValue) => {
    if (newValue) {
      await scrollToReplyArea();
    }
  },
);

/**
 * Returns the total number of replies for a comment, including nested replies.
 * @param comment The comment object to get the total replies for.
 */
const getTotalReplies = (comment: DiscussionComment): number => {
  if (!comment.replies) return 0;
  return (
    comment.replies.length +
    comment.replies.reduce((acc, reply) => acc + getTotalReplies(reply), 0)
  );
};
</script>

<template>
  <div
    class="DiscussionComponentComment"
    :style="{ '--comment-accent': commentColor }"
  >
    <div class="comment-container">
      <div class="meta">
        <div class="left-meta">
          <!--
            The author colour identifies the speaker on the accent bar, where it
            only needs 3:1. As text on white those pastels fall under 4.5:1, so
            the name uses the standard body colour instead.
          -->
          <strong class="author">
            {{ comment.authorName }}
          </strong>
          <span class="timestamp"
            >•
            {{
              comment.editedAt
                ? formatDate(comment.editedAt, 7)
                : formatDate(comment.createdAt, 7)
            }}</span
          >
          <el-popover
            v-if="comment.editedAt"
            trigger="hover"
            placement="top"
            width="fit-content"
            :popper-style="{ fontSize: '0.8em' }"
            :content="`Original post: ${formatDate(comment.createdAt, 7)}`"
          >
            <template #reference>
              <span class="edited-at"> (Edited) </span>
            </template>
          </el-popover>
        </div>

        <div v-if="!(showReply || editing)" class="buttons">
          <el-button
            v-if="comment.authorGuid === currentUser.userGuid && !readOnly"
            class="edit-btn"
            text
            size="small"
            @click="editing = props.comment"
          >
            Edit
          </el-button>
          <el-button
            v-if="currentDepth <= maxDepth && !readOnly"
            class="reply-btn"
            text
            size="small"
            @click="showReply = true"
          >
            Reply
          </el-button>
        </div>
      </div>

      <div v-if="!editing" class="text">
        <!-- eslint-disable-next-line vue/no-v-html -- sanitized by sanitizeCommentHtml -->
        <div class="comment-body" v-html="commentHtml"></div>
      </div>

      <div v-if="showReply || editing" ref="replyAreaRef" class="reply-input">
        <DiscussionThreadEditor
          v-model="replyText"
          class="reply-input-area"
          :disabled="readOnly"
          :placeholder="substituteTemplatedText(properties.placeholder) ?? ''"
        />

        <el-button
          class="post-btn"
          type="primary"
          :disabled="isReplyEmpty || readOnly"
          @click="submitReply"
        >
          {{ properties.saveButtonText }}
        </el-button>

        <el-button
          text
          class="cancel-btn"
          :disabled="readOnly"
          @click="handleCancel"
        >
          {{ properties.cancelButtonText || "Cancel" }}
        </el-button>
      </div>
    </div>

    <div v-if="comment.replies?.length" class="reply-toggle-wrapper">
      <el-button
        :class="['collapse-btn', { expanded: !collapsed }]"
        text
        :bg="collapsed ? true : false"
        size="small"
        @click="collapsed = !collapsed"
      >
        {{ collapsed ? "View replies" : "Hide replies" }}
      </el-button>

      <span v-if="collapsed" class="reply-summary">
        {{
          `${getTotalReplies(comment)} repl${plural(getTotalReplies(comment), "y", "ies")} as of ${formatDate(comment.replies[comment.replies.length - 1].editedAt || comment.replies[comment.replies.length - 1].createdAt, 7)}`
        }}
      </span>
    </div>

    <div v-if="!collapsed && comment.replies?.length" class="replies">
      <DiscussionThreadComment
        v-for="reply in comment.replies"
        :key="reply.id"
        :comment="reply"
        :color-hierarchy="colorHierarchy"
        :author-color-map="authorColorMap"
        :current-user="currentUser"
        :current-user-guid="currentUserGuid"
        :current-depth="currentDepth + 1"
        :properties="properties"
        :read-only="readOnly"
        :substitute-templated-text="substituteTemplatedText"
        @reply="
          (parentId: number, reply: DiscussionComment) =>
            emit('reply', parentId, reply)
        "
      />
    </div>
  </div>
</template>

<style lang="less" scoped>
// 4px-based spacing rhythm, so gaps between the meta row, body, and replies are
// deliberate steps rather than ad-hoc em values.
@space-1: 4px;
@space-2: 8px;
@space-3: 12px;
@space-4: 16px;

@comment-border: #e6e8eb;
@text-primary: #2f3437;
@text-muted: #6b7280;

.DiscussionComponentComment {
  display: grid;
  margin: @space-2 0;

  .comment-container {
    background: #fff;
    // The author-colour accent moves onto the card itself, so nesting is shown
    // by one device (indent + accent) rather than competing borders.
    border: 1px solid @comment-border;
    border-left: 3px solid var(--comment-accent, @comment-border);
    border-radius: 6px;
    // Padding was 0, which left text jammed against the accent bar.
    padding: @space-3 @space-4;
    transition:
      border-color 0.2s ease,
      box-shadow 0.2s ease;

    &:hover {
      box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
    }

    .meta {
      display: flex;
      align-items: baseline;
      justify-content: space-between;
      gap: @space-2;
      font-size: 0.85em;
      color: @text-muted;
      width: 100%;
      margin-bottom: @space-2;

      .left-meta {
        display: flex;
        align-items: baseline;
        gap: @space-1;
        min-width: 0;
      }

      // The author is an identity label, not the message — keep it readable but
      // let the comment body hold the visual weight.
      .author {
        font-weight: 600;
        font-size: 0.95em;
        color: @text-primary;
      }

      .buttons {
        display: flex;
        flex-wrap: nowrap;
        flex-shrink: 0;
        // Actions stay quiet until the comment is hovered or focused within, so
        // a long thread reads as content rather than rows of buttons. Dimming
        // rather than hiding keeps them discoverable and always tab-reachable.
        opacity: 0.45;
        transition: opacity 0.15s ease;
      }

      // Touch devices have no hover, so the dimmed state would be permanent —
      // show the actions outright there.
      @media (hover: none) {
        .buttons {
          opacity: 1;
        }
      }

      .reply-btn,
      .edit-btn {
        padding: 0 @space-1;
        max-width: fit-content;
      }

      .reply-btn,
      .edit-btn,
      .timestamp,
      .edited-at {
        color: @text-muted;
      }
    }

    // Keyboard users never hover, so focus must reveal the actions too.
    &:hover .meta .buttons,
    &:focus-within .meta .buttons {
      opacity: 1;
    }
  }

  .reply-toggle-wrapper {
    display: flex;
    align-items: center;
    margin-top: @space-1;
    padding-left: @space-2;

    &:has(.expanded) {
      justify-content: start;
    }
    .collapse-btn {
      color: @text-muted;
      justify-self: start;
      max-width: fit-content;
    }

    .reply-summary {
      margin-left: @space-2;
      font-size: 0.8em;
      color: @text-muted;
    }
  }

  .text {
    font-size: 1em;
    // 1.5 line-height for body copy per the typography rules; the old default
    // made multi-line comments cramped.
    line-height: 1.5;
    color: @text-primary;

    // Rich-text bodies arrive as HTML, so tame the browser defaults: TinyMCE
    // wraps every line in <p>, which would otherwise add a full blank line
    // above and below each one-line comment.
    .comment-body {
      overflow-wrap: break-word;

      :first-child {
        margin-top: 0;
      }
      :last-child {
        margin-bottom: 0;
      }

      p {
        margin: 0 0 0.5em;
      }

      ul,
      ol {
        margin: 0.25em 0;
        padding-left: 1.5em;
      }

      a {
        color: var(--el-color-primary);
      }

      // Pasted images keep their natural size, which is routinely wider than
      // the comment column — clamp them so one screenshot can't blow out the
      // thread layout.
      img {
        display: block;
        max-width: 100%;
        max-height: 400px;
        height: auto;
        object-fit: contain;
        margin: 0.5em 0;
        border-radius: 4px;
      }
    }
  }

  // Nesting reads through indent plus one quiet guide line. The dashed rule
  // fought with each card's accent border and made deep threads noisy.
  .replies {
    border-left: 1px solid @comment-border;
    margin-left: @space-2;
    padding-left: @space-3;
  }
}
</style>
